import {
  Box,
  Text as CText,
  HStack,
  StyleProps,
  VStack,
} from '@chakra-ui/react'
import React, { Ref } from 'react'
import { ResponsiveContainer } from 'recharts'

import { useIsDark } from '../../../hooks/useIsDark'
import type { ChartAreaMouseHandlers } from '../chartTooltip/ChartTooltipPointerContext'
import '../common/Chart.css'
import {
  CHART_HEIGHT,
  CHART_MIN_PLOT_AREA_HEIGHT,
  CHART_MIN_WIDTH,
  CHART_X_AXIS_LABEL_ROW_GAP,
  CHART_X_AXIS_LABEL_ROW_HEIGHT,
  SNAPSHOT_Y_AXIS_LABEL_GAP,
} from './chart.constants'
import { useChartColors } from './useChartColors'

export interface RechartsWrapperProps extends StyleProps {
  children?: React.ReactNode
  className?: string
  yAxisLabel?: string
  xAxisLabel?: string
  /** Override the standard chart height (px). */
  chartHeight?: number
  /**
   * When set, the chart area becomes this wide and scrolls horizontally
   * if the content exceeds the container width. Useful for bar/line charts with many data points.
   */
  scrollableWidth?: number
  /**
   * When set, the chart area becomes this tall and scrolls vertically
   * if the content exceeds chartHeight. Useful for horizontal bar charts.
   */
  scrollableHeight?: number
  /**
   * Track pointer over the chart plot area for floating (portal) tooltips.
   * Typically wired from `FloatingChartTooltipShell`.
   */
  chartAreaMouseTracking?: ChartAreaMouseHandlers
  /** Gap between Y-axis label and plot. Snapshot dashboard uses 8px. */
  yAxisLabelGap?: string
  /** Snapshot dashboard: Montserrat labels matching Figma. */
  variant?: 'default' | 'snapshot'
  ref?: Ref<HTMLDivElement>
}

interface ChartContentProps {
  children: React.ReactNode
  chartAreaHeight: number
  scrollableWidth?: number
  scrollableHeight?: number
  chartAreaMouseTracking?: ChartAreaMouseHandlers
}

const horizontalScrollOuterMinW = `${CHART_MIN_WIDTH}px`

/** Plot area height for a given outer height, less the X-axis label row and its gap. */
export const resolveChartAreaHeight = (
  chartHeight: number,
  hasXAxisLabel = false,
) => {
  const labelRowChrome = hasXAxisLabel
    ? CHART_X_AXIS_LABEL_ROW_HEIGHT + CHART_X_AXIS_LABEL_ROW_GAP
    : 0
  return labelRowChrome > 0
    ? Math.max(chartHeight - labelRowChrome, CHART_MIN_PLOT_AREA_HEIGHT)
    : chartHeight
}

const HorizontalScrollContent = ({
  children,
  chartAreaHeight,
  scrollableWidth,
  chartAreaMouseTracking,
}: ChartContentProps) => {
  const chartWithSize = React.cloneElement(children as React.ReactElement, {
    width: scrollableWidth,
    height: chartAreaHeight,
  })

  return (
    <Box
      overflowX="scroll"
      maxW="full"
      minW={horizontalScrollOuterMinW}
      className="chart-scroll"
    >
      <Box
        width={`${scrollableWidth}px`}
        minWidth={`${scrollableWidth}px`}
        height={`${chartAreaHeight}px`}
        {...chartAreaMouseTracking}
      >
        {chartWithSize}
      </Box>
    </Box>
  )
}

const VerticalScrollContent = ({
  children,
  chartAreaHeight,
  scrollableHeight,
  chartAreaMouseTracking,
}: ChartContentProps) => (
  <Box overflowY="auto" overflowX="hidden" maxHeight={chartAreaHeight}>
    <Box height={`${scrollableHeight}px`} {...chartAreaMouseTracking}>
      <ResponsiveContainer width="100%" height={scrollableHeight!}>
        {children as any}
      </ResponsiveContainer>
    </Box>
  </Box>
)

const StaticContent = ({
  children,
  chartAreaHeight,
  chartAreaMouseTracking,
}: ChartContentProps) => (
  <Box display="flex" flexDirection="column" {...chartAreaMouseTracking}>
    <ResponsiveContainer width="100%" height={chartAreaHeight}>
      {children as any}
    </ResponsiveContainer>
  </Box>
)

const resolveContentStrategy = (props: ChartContentProps) => {
  const { scrollableWidth, scrollableHeight, chartAreaHeight } = props
  const needsHorizontalScroll = scrollableWidth !== undefined
  const needsVerticalScroll =
    scrollableHeight !== undefined && scrollableHeight > chartAreaHeight

  if (needsHorizontalScroll) return <HorizontalScrollContent {...props} />
  if (needsVerticalScroll) return <VerticalScrollContent {...props} />
  return <StaticContent {...props} />
}

export const RechartsWrapper = ({
  children,
  yAxisLabel,
  xAxisLabel,
  chartHeight = CHART_HEIGHT,
  scrollableWidth,
  scrollableHeight,
  chartAreaMouseTracking,
  minW = `${CHART_MIN_WIDTH}px`,
  yAxisLabelGap,
  variant = 'default',
  className,
  ref,
  ...props
}: RechartsWrapperProps) => {
  const isDark = useIsDark()
  const hasXAxisLabel = Boolean(xAxisLabel)
  const chartAreaHeight = resolveChartAreaHeight(chartHeight, hasXAxisLabel)
  const isSnapshot = variant === 'snapshot'
  const chartColors = useChartColors()
  const labelGap =
    yAxisLabelGap ?? (isSnapshot ? SNAPSHOT_Y_AXIS_LABEL_GAP : '36px')

  const yAxisLabelAlign = yAxisLabel && isSnapshot ? 'center' : 'stretch'
  const axisLabelColor = isSnapshot
    ? 'presets.neutral.0'
    : chartColors.axisLabelColor
  const axisLabelFontSize = isSnapshot ? '12px' : '10px'
  const axisLabelFontFamily = `'Montserrat', sans-serif`

  const chartRootClassName = ['chart-recharts-root', className]
    .filter(Boolean)
    .join(' ')

  return (
    <VStack
      ref={ref}
      minW={minW}
      width="100%"
      alignItems="stretch"
      gap={2}
      className={chartRootClassName}
      data-color-mode={isDark ? 'dark' : 'light'}
      {...props}
    >
      <HStack alignItems={yAxisLabelAlign} gap={labelGap} minW={0}>
        {yAxisLabel ? (
          <CText
            alignSelf={isSnapshot ? 'center' : undefined}
            style={{
              writingMode: 'vertical-rl',
            }}
            transform="rotate(180deg)"
            textAlign="center"
            textColor={axisLabelColor}
            fontWeight={500}
            fontSize={axisLabelFontSize}
            lineHeight={isSnapshot ? 'normal' : '1.4em'}
            fontFamily={axisLabelFontFamily}
            whiteSpace="nowrap"
            flexShrink={0}
          >
            {yAxisLabel}
          </CText>
        ) : null}

        <VStack
          alignItems="stretch"
          gap={`${CHART_X_AXIS_LABEL_ROW_GAP}px`}
          minW={0}
          flex={1}
        >
          {resolveContentStrategy({
            children,
            chartAreaHeight,
            scrollableWidth,
            scrollableHeight,
            chartAreaMouseTracking,
          })}

          {hasXAxisLabel ? (
            <CText
              minHeight={`${CHART_X_AXIS_LABEL_ROW_HEIGHT}px`}
              textColor={axisLabelColor}
              fontWeight={500}
              fontSize={axisLabelFontSize}
              lineHeight="1.4em"
              fontFamily={axisLabelFontFamily}
              textAlign="center"
            >
              {xAxisLabel}
            </CText>
          ) : null}
        </VStack>
      </HStack>
    </VStack>
  )
}
